// AgentActivityV2 — friendly, streamed timeline of what the agent is doing.
//
// Subscribes to the live interaction via `useAiChatSessionStore`:
//   - thought parts                → italic reasoning lines
//   - function_call parts          → tool rows with icon · friendly label
//                                    · mono tool name · duration · status
//   - partial_function_call parts  → "compondo argumentos…"
//   - function_response parts      → folded into the matching call row's
//                                    expanded body (matched by call id)
//   - text/partial_text/etc.       → ignored (those are the assistant's
//                                    natural-language reply, not activity)
//
// When no live interaction is in flight, falls back to the last completed
// interaction so the panel keeps showing the most recent run.
//
// Click any tool row to expand it and inspect its args + response.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useAiChatSessionStore } from '../AiChat/AiChatContext';
import type { Interaction } from '../../models/Interaction';
import type { InteractionPart } from '../../models/InteractionPart';
import { ChevronIcon, ToolIcon } from './icons';
import { getFriendlyLabel, type FriendlyState } from '../AiChat/ChatPane/friendlyToolConfig';
import { InteractionPartUtils } from '../../models/InteractionPart';

const spinKeyframes = `@keyframes v2aaspin{to{transform:rotate(360deg)}}`;
const fadeKeyframes = `@keyframes v2aafade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}`;
const pulseKeyframes = `@keyframes v2aapulse{0%,100%{opacity:.4}50%{opacity:1}}`;

function formatDuration(ms?: number): string | null {
    if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
}

/**
 * Render a string that may itself be a JSON payload — and may carry
 * unicode-escape sequences like `\u0022` for `"` because the server
 * encoded with `ensure_ascii=True`. If it parses as JSON, re-stringify
 * with indentation (the JSON.parse round-trip naturally collapses the
 * `\uXXXX` escapes into real characters). Otherwise leave the string
 * as-is so we don't mangle plain text responses.
 */
function prettyJsonish(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return value;
    const first = trimmed[0];
    if (first !== '{' && first !== '[' && first !== '"') return value;
    try {
        const parsed = JSON.parse(trimmed);
        // If the payload was a JSON-encoded string (double-encoded),
        // recurse once so the inner JSON also pretty-prints.
        if (typeof parsed === 'string') return prettyJsonish(parsed);
        return JSON.stringify(parsed, null, 2);
    } catch {
        return value;
    }
}

// V1's bucket sizing kept the tactile, "filling-up" feel without adding
// noise. We keep the same chunking so the texture reads consistently
// across versions, just toned-down for the v2 dark palette.
const BYTES_PER_BUCKET = 500;
const MAX_VISIBLE_BUCKETS = 12;
const BUCKET_W = 4;
const BUCKET_H = 8;

function compactBytes(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}Mb`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}kb`;
    return `${value}b`;
}

function V2ByteBuckets({ bytes }: { bytes: number }) {
    if (bytes <= 0) return null;
    const fullCount = Math.floor(bytes / BYTES_PER_BUCKET);
    const partialFill = (bytes % BYTES_PER_BUCKET) / BYTES_PER_BUCKET;
    const visible = Math.min(MAX_VISIBLE_BUCKETS, fullCount + (partialFill > 0 ? 1 : 0)) || 1;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'flex-end',
                gap: '2px',
                height: `${BUCKET_H + 2}px`,
            }}
            aria-hidden
        >
            {Array.from({ length: visible }, (_, i) => {
                const isFull = i < fullCount;
                const isActive = !isFull && i === fullCount && partialFill > 0;
                const fillRatio = isFull ? 1 : isActive ? partialFill : 0;
                return (
                    <Box
                        key={i}
                        sx={(theme) => ({
                            position: 'relative',
                            width: `${BUCKET_W}px`,
                            height: `${BUCKET_H}px`,
                            borderRadius: '1px',
                            border: `1px solid ${theme.palette.border.soft}`,
                            backgroundColor: theme.palette.surface.raised,
                            overflow: 'hidden',
                            flexShrink: 0,
                        })}
                    >
                        <Box
                            sx={(theme) => ({
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: `${fillRatio * 100}%`,
                                backgroundColor: theme.palette.primary.main,
                                opacity: isFull ? 0.85 : 0.7,
                                transition: 'height .15s ease-out',
                                animation: isActive ? 'v2aapulse 0.9s ease-in-out infinite' : 'none',
                            })}
                        />
                    </Box>
                );
            })}
        </Box>
    );
}

function ToolGlyph({ name, status }: { name: string; status: 'pending' | 'running' | 'done' }) {
    const Icon = (ToolIcon as Record<string, (p: any) => React.ReactElement>)[name] ?? ToolIcon.generic;
    const isRunning = status === 'running';
    const isDone = status === 'done';
    // Running state uses a squared rounded "ring" with one side missing
    // that rotates around the icon — matches the v2 design's spinner.
    // The icon stays visible inside the rotating frame so the user still
    // recognises which tool is active at a glance.
    return (
        <Box
            sx={{
                position: 'relative',
                width: 26,
                height: 26,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                aria-hidden
                sx={(theme) => ({
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '6px',
                    backgroundColor: isRunning
                        ? 'transparent'
                        : isDone
                            ? theme.palette.surface.raised
                            : 'transparent',
                    border: isRunning
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.border.soft}`,
                    borderRightColor: isRunning ? 'transparent' : undefined,
                    animation: isRunning ? 'v2aaspin 1.1s linear infinite' : 'none',
                })}
            />
            <Box
                sx={(theme) => ({
                    position: 'relative',
                    color: isRunning
                        ? theme.palette.primary.main
                        : isDone
                            ? theme.palette.text.secondary
                            : theme.palette.text.disabled,
                    display: 'inline-flex',
                })}
            >
                <Icon size={14} />
            </Box>
        </Box>
    );
}

interface ToolRowProps {
    part: InteractionPart;
    isLast: boolean;
    /** When true, hide the args preview line (default — matches the v2 design). */
    compact?: boolean;
}

function ToolRow({ part, isLast, compact = true }: ToolRowProps) {
    const [expanded, setExpanded] = useState(false);
    const fn = part.functionDetails;
    if (!fn) return null;

    const start = fn.start ? new Date(fn.start).getTime() : undefined;
    const end = fn.end ? new Date(fn.end).getTime() : undefined;
    // Both `Composing` (the agent is still assembling args) and
    // `Executing` (the call has been dispatched) are "in flight" from
    // the user's perspective — only `Done` counts as completed.
    const status: 'running' | 'done' | 'pending' =
        fn.status === 'Done'
            ? 'done'
            : fn.status === 'Executing' || fn.status === 'Composing'
                ? 'running'
                : 'pending';
    const duration = status === 'done' && start && end ? formatDuration(end - start) : null;
    const isRunning = status === 'running';
    const isComposing = fn.status === 'Composing';

    const argsPreview = useMemo(() => {
        if (fn.text) return prettyJsonish(fn.text);
        if (fn.args == null) return '';
        if (typeof fn.args === 'string') return prettyJsonish(fn.args);
        try {
            return JSON.stringify(fn.args);
        } catch {
            return String(fn.args);
        }
    }, [fn.args, fn.text]);

    const responsePreview = useMemo(() => {
        if (fn.response == null) return '';
        try {
            return typeof fn.response === 'string'
                ? prettyJsonish(fn.response)
                : JSON.stringify(fn.response, null, 2);
        } catch {
            return String(fn.response);
        }
    }, [fn.response]);

    // Map the part's lifecycle to the friendly-label state V1 uses, then
    // get the human-readable Portuguese label from the shared registry
    // (e.g. "Consultando série de vendas" instead of "Run query").
    const friendlyState: FriendlyState =
        part.eventType === 'function_response'
            ? 'response'
            : part.eventType === 'partial_function_call' || isComposing
                ? 'partial'
                : 'call';
    const isError =false;
    const friendlyLabel = useMemo(
        () => getFriendlyLabel(fn.name, friendlyState, fn.args ?? fn.text, isError),
        [fn.name, friendlyState, fn.args, fn.text, isError],
    );

    // Streamed-byte counter for the bucket texture — only meaningful while
    // the agent is composing the args (partial state). During composing
    // the runtime accumulates JSON chunks into `part.textDetails.textParts`
    // (one chunk per StreamingFunctionCallUpdateContent event); `fn.text`
    // only carries the LATEST chunk and would read ~9 bytes. Use V1's
    // `getPartTextContent` so we read the full accumulated stream.
    const streamedBytes = useMemo(() => {
        const accumulated = InteractionPartUtils.getPartTextContent(part);
        if (typeof accumulated === 'string' && accumulated.length > 0) return accumulated.length;
        return argsPreview.length;
    }, [part, argsPreview]);

    return (
        <Box
            className="v2aa-step"
            sx={{ position: 'relative', '&:not(:last-child)': { mb: 0.25 } }}
        >
            {!isLast && (
                <Box
                    sx={(theme) => ({
                        position: 'absolute',
                        left: 26,
                        top: 28,
                        bottom: -4,
                        width: '1.5px',
                        backgroundColor: theme.palette.border.soft,
                    })}
                />
            )}
            <Box
                role="button"
                tabIndex={0}
                onClick={() => setExpanded((v) => !v)}
                onKeyDown={(e) => { if (e.key === 'Enter') setExpanded((v) => !v); }}
                sx={(theme) => ({
                    width: '100%',
                    textAlign: 'left',
                    px: 1.5,
                    py: 0.875,
                    cursor: 'pointer',
                    color: theme.palette.text.primary,
                    fontFamily: theme.typography.fontFamily,
                    borderRadius: `${theme.shape.radiusSm}px`,
                    // Active row is highlighted via text color + the squared
                    // spinner only — no left-border, no background fill.
                    '&:hover': { backgroundColor: theme.palette.surface.raised },
                })}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <ToolGlyph name={fn.name} status={status} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                                sx={(theme) => ({
                                    fontSize: 12.5,
                                    fontWeight: isRunning ? 500 : 400,
                                    fontStyle: isError ? 'normal' : isComposing ? 'italic' : 'normal',
                                    color: isError
                                        ? theme.palette.secondary.main
                                        : status === 'pending'
                                            ? theme.palette.text.disabled
                                            : isRunning
                                                ? theme.palette.text.primary
                                                : theme.palette.text.secondary,
                                    textAlign: 'left',
                                })}
                            >
                                {friendlyLabel}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            {/* Composing — bucket texture + byte counter so the
                                user feels args streaming in. Once dispatched the
                                spinner glyph + "executando…" label takes over. */}
                            {isComposing && streamedBytes > 0 && (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <V2ByteBuckets bytes={streamedBytes} />
                                    <Typography sx={(theme) => ({ fontSize: 10, color: theme.palette.text.disabled, fontFamily: theme.typography.fontFamilyMono })}>
                                        {compactBytes(streamedBytes)}
                                    </Typography>
                                </Box>
                            )}
                            {duration && (
                                <Typography sx={(theme) => ({ fontSize: 10.5, color: theme.palette.text.disabled, fontFamily: theme.typography.fontFamilyMono })}>
                                    {duration}
                                </Typography>
                            )}
                            {isRunning && !isComposing && (
                                <Typography
                                    sx={(theme) => ({
                                        fontSize: 10.5,
                                        color: theme.palette.primary.main,
                                        fontFamily: theme.typography.fontFamilyMono,
                                        animation: 'v2aapulse 1s ease-in-out infinite',
                                    })}
                                >
                                    executando…
                                </Typography>
                            )}
                            {status === 'pending' && (
                                <Typography sx={(theme) => ({ fontSize: 10.5, color: theme.palette.text.disabled, fontFamily: theme.typography.fontFamilyMono })}>
                                    aguardando
                                </Typography>
                            )}
                        </Box>
                        {/* In compact mode (default) the args preview line is hidden until
                            the user clicks the row to expand. The full args + response
                            still render below in the expanded code block. */}
                        {(!compact || expanded) && argsPreview && (
                            <Typography
                                sx={(theme) => ({
                                    fontSize: 11,
                                    color: theme.palette.text.secondary,
                                    fontFamily: theme.typography.fontFamilyMono,
                                    mt: 0.4,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: expanded ? 'normal' : 'nowrap',
                                    wordBreak: expanded ? 'break-all' : 'normal',
                                    maxWidth: '100%',
                                    textAlign: 'left',
                                })}
                            >
                                {argsPreview}
                            </Typography>
                        )}
                        {expanded && responsePreview && (
                            <Box
                                component="pre"
                                sx={(theme) => ({
                                    mt: 1,
                                    p: 1.25,
                                    backgroundColor: theme.palette.background.default,
                                    border: `1px solid ${theme.palette.border.soft}`,
                                    borderRadius: `${theme.shape.radiusSm}px`,
                                    fontSize: 11,
                                    color: theme.palette.text.secondary,
                                    fontFamily: theme.typography.fontFamilyMono,
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: 240,
                                    overflow: 'auto',
                                    m: 0,
                                })}
                            >
                                {responsePreview}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function ReasoningRow({ part, isStreaming }: { part: InteractionPart; isStreaming: boolean }) {
    const text = part.thoughtDetails?.text?.trim();
    if (!text) return null;
    return (
        <Box sx={{ position: 'relative', px: 1.75, py: 0.75 }}>
            <Box
                sx={(theme) => ({
                    position: 'absolute',
                    left: 30,
                    top: '14px',
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.surface.hover,
                    border: `1.5px solid ${theme.palette.border.soft}`,
                })}
            />
            <Typography
                sx={(theme) => ({
                    pl: 5,
                    fontSize: 11.5,
                    color: theme.palette.text.secondary,
                    fontStyle: 'italic',
                    lineHeight: 1.55,
                    textAlign: 'left',
                })}
            >
                {text}
                {isStreaming && (
                    <Box
                        component="span"
                        sx={(theme) => ({
                            ml: 0.5,
                            color: theme.palette.primary.main,
                            animation: 'v2aapulse 1s ease-in-out infinite',
                        })}
                    >
                        ▍
                    </Box>
                )}
            </Typography>
        </Box>
    );
}

export interface AgentActivityV2Props {
    /** Hide reasoning rows ("Pensando…"). */
    hideReasoning?: boolean;
    /** Maximum visible height before the timeline scrolls. Defaults to 320px. */
    maxHeight?: number | string;
    /** Empty state to show when there are no parts to render. */
    emptyState?: React.ReactNode;
    /**
     * Display order. `'chronological'` (default) keeps stream order with
     * the live row at the bottom and auto-scrolls as new parts arrive;
     * `'newest-first'` puts the most recent part on top.
     */
    order?: 'newest-first' | 'chronological';
    /**
     * When true (default) tool rows hide their args/JSON preview line and
     * only reveal it when the user clicks the row to expand. Set to false
     * to always show the preview inline.
     */
    compact?: boolean;
    /**
     * Render a horizontal pill strip at the top to navigate by turn:
     * an "All turns" pill (merged history) plus one pill per past
     * interaction. While a turn is in flight the live activities are
     * shown by default; clicking a pill switches the view. When a turn
     * closes the panel goes empty until the user picks a pill — i.e.
     * we don't keep showing the previous turn's stale activities.
     * Defaults to `false`.
     */
    showTurnHistory?: boolean;
}

interface TurnEntry {
    id: string;
    interaction: Interaction;
    number: number;
    timeLabel: string;
}

function getInteractionId(interaction: Interaction | null): string | null {
    return interaction?.interactionId ?? null;
}

function formatHourMinute(value: Date | string | undefined): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

const SCROLL_STEP = 200;

function TurnPillStrip({
    turns,
    selectedKey,
    onSelect,
}: {
    turns: TurnEntry[];
    selectedKey: 'all' | string | null;
    onSelect: (key: 'all' | string | null) => void;
}) {
    const stripRef = useRef<HTMLDivElement | null>(null);
    const [overflowLeft, setOverflowLeft] = useState(false);
    const [overflowRight, setOverflowRight] = useState(false);

    const recomputeOverflow = useCallback(() => {
        const el = stripRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setOverflowLeft(el.scrollLeft > 4);
        setOverflowRight(el.scrollLeft < max - 4);
    }, []);

    useEffect(() => {
        recomputeOverflow();
        const el = stripRef.current;
        if (!el) return;
        const handler = () => recomputeOverflow();
        el.addEventListener('scroll', handler, { passive: true });
        const ro = new ResizeObserver(recomputeOverflow);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', handler);
            ro.disconnect();
        };
    }, [recomputeOverflow, turns.length]);

    const scrollBy = (delta: number) => {
        stripRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
    };

    const renderPill = (
        key: string,
        label: React.ReactNode,
        isSelected: boolean,
        onClick: () => void,
    ) => (
        <Box
            key={key}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
            sx={(theme) => ({
                flex: '0 0 auto',
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                cursor: 'pointer',
                backgroundColor: isSelected ? theme.palette.surface.raised : 'transparent',
                // Every pill carries a border so they read as discrete
                // chips even when nothing is selected. The selected
                // state just bumps the border to the brighter `border`
                // tone vs. the muted `borderSoft` on the rest.
                border: `1px solid ${isSelected ? theme.palette.divider : theme.palette.border.soft}`,
                color: isSelected ? theme.palette.text.primary : theme.palette.text.secondary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 11.5,
                fontWeight: isSelected ? 500 : 400,
                whiteSpace: 'nowrap',
                transition: 'background-color .12s, color .12s',
                '&:hover': {
                    backgroundColor: isSelected ? theme.palette.surface.raised : theme.palette.surface.raised,
                    color: theme.palette.text.primary,
                },
            })}
        >
            {label}
        </Box>
    );

    return (
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', mb: 0.75 }}>
            {overflowLeft && (
                <IconButton
                    size="small"
                    onClick={() => scrollBy(-SCROLL_STEP)}
                    sx={(theme) => ({
                        flexShrink: 0,
                        color: theme.palette.text.secondary,
                        p: 0.25,
                        mr: 0.25,
                        '& svg': { transform: 'rotate(90deg)' },
                        '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.surface.raised },
                    })}
                    aria-label="Rolar para a esquerda"
                >
                    <ChevronIcon size={14} />
                </IconButton>
            )}
            <Box
                ref={stripRef}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {renderPill(
                    'all',
                    <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                        All turns
                    </Typography>,
                    selectedKey === 'all',
                    () => onSelect('all'),
                )}
                {turns.map((t) => {
                    const isSelected = selectedKey === t.id;
                    return renderPill(
                        t.id,
                        <>
                            <Typography component="span" sx={(theme) => ({ fontSize: 'inherit', fontWeight: 'inherit', fontFamily: theme.typography.fontFamily })}>
                                Turn {t.number}
                            </Typography>{' '}
                            <Typography
                                component="span"
                                sx={(theme) => ({
                                    fontSize: 10.5,
                                    fontFamily: theme.typography.fontFamilyMono,
                                    color: theme.palette.text.disabled,
                                    ml: 0.5,
                                })}
                            >
                                {t.timeLabel}
                            </Typography>
                        </>,
                        selectedKey === t.id,
                        () => onSelect(t.id),
                    );
                })}
            </Box>
            {overflowRight && (
                <IconButton
                    size="small"
                    onClick={() => scrollBy(SCROLL_STEP)}
                    sx={(theme) => ({
                        flexShrink: 0,
                        color: theme.palette.text.secondary,
                        p: 0.25,
                        ml: 0.25,
                        '& svg': { transform: 'rotate(-90deg)' },
                        '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.surface.raised },
                    })}
                    aria-label="Rolar para a direita"
                >
                    <ChevronIcon size={14} />
                </IconButton>
            )}
        </Box>
    );
}

export const AgentActivityV2: React.FC<AgentActivityV2Props> = ({
    hideReasoning,
    maxHeight = 420,
    emptyState,
    order = 'chronological',
    compact = true,
    showTurnHistory = false,
}) => {
    const currentMessage = useAiChatSessionStore((s) => s.currentMessage);
    const messages = useAiChatSessionStore((s) => s.messages);
    const turnOpen = useAiChatSessionStore((s) => s.turnOpen);

    // Build the per-turn pill metadata. Only past interactions that
    // surfaced any tool/thought activity get a pill (an interaction with
    // just a text reply isn't useful to revisit here). We number them
    // ascending by `created` so "Turn 1" is the oldest, matching the way
    // a human would refer to them in conversation.
    const turns: TurnEntry[] = useMemo(() => {
        if (!showTurnHistory || !messages || messages.length === 0) return [];
        const wanted = new Set(['thought', 'function_call', 'function_response', 'partial_function_call']);
        const sorted = [...messages].sort((a, b) => {
            const ta = a.created ? new Date(a.created).getTime() : 0;
            const tb = b.created ? new Date(b.created).getTime() : 0;
            return ta - tb;
        });
        const entries: TurnEntry[] = [];
        sorted.forEach((interaction, idx) => {
            const id = getInteractionId(interaction);
            if (!id) return;
            const hasActivity = interaction.parts?.some(
                (p) => wanted.has(p.eventType) && !p.functionDetails?.silent,
            );
            if (!hasActivity) return;
            entries.push({
                id,
                interaction,
                number: idx + 1,
                timeLabel: formatHourMinute(interaction.created),
            });
        });
        // Newest first — matches the screenshot's left-to-right order
        // (newest "Turn 55" sits to the left of older ones).
        return entries.reverse();
    }, [messages, showTurnHistory]);

    // `selectedKey` controls which interaction's parts to render:
    //   - null  → live view (current message while turnOpen) or empty
    //             when turn closed and the user hasn't picked anything
    //   - 'all' → every past interaction's parts merged chronologically
    //   - <id>  → only that one interaction's parts
    const [selectedKey, setSelectedKey] = useState<'all' | string | null>(null);
    const prevTurnOpenRef = useRef(turnOpen);

    // Reset selection when a new turn starts so the user gets the live
    // view back automatically. We don't auto-clear when a turn ends —
    // per the v2 spec the panel goes empty until the user picks a pill.
    useEffect(() => {
        const wasOpen = prevTurnOpenRef.current;
        prevTurnOpenRef.current = turnOpen;
        if (turnOpen && !wasOpen) {
            setSelectedKey(null);
        }
    }, [turnOpen]);

    const visibleParts = useMemo(() => {
        const wanted = new Set(['thought', 'function_call', 'function_response', 'partial_function_call']);
        const collect = (interaction: Interaction | null): InteractionPart[] => {
            if (!interaction?.parts) return [];
            return interaction.parts.filter(
                (p) => wanted.has(p.eventType) && !p.functionDetails?.silent,
            );
        };

        let parts: InteractionPart[] = [];

        if (selectedKey === 'all') {
            // Merge every past interaction's parts in chronological order
            // (oldest first). The live current-turn parts are appended at
            // the end so a still-running turn stays visible inside the
            // merged view too.
            const sorted = [...(messages ?? [])].sort((a, b) => {
                const ta = a.created ? new Date(a.created).getTime() : 0;
                const tb = b.created ? new Date(b.created).getTime() : 0;
                return ta - tb;
            });
            sorted.forEach((m) => { parts = parts.concat(collect(m)); });
            if (currentMessage && getInteractionId(currentMessage) !== null) {
                const liveId = getInteractionId(currentMessage);
                const alreadyIncluded = sorted.some((m) => getInteractionId(m) === liveId);
                if (!alreadyIncluded) parts = parts.concat(collect(currentMessage));
            }
        } else if (selectedKey) {
            const liveId = getInteractionId(currentMessage);
            const target =
                liveId === selectedKey
                    ? currentMessage
                    : (messages ?? []).find((m) => getInteractionId(m) === selectedKey) ?? null;
            parts = collect(target);
        } else if (turnOpen && currentMessage) {
            // Live view (no pill selected and a turn is in flight).
            parts = collect(currentMessage);
        }
        // Else: turn closed AND no pill selected → render nothing. This
        // is the "should not show anything once the turn closes" rule.

        return order === 'newest-first' ? [...parts].reverse() : parts;
    }, [selectedKey, currentMessage, messages, turnOpen, order]);

    // Auto-scroll-to-bottom when chronological so the live row is always
    // in view as the agent streams new tool calls. We delegate the
    // actual scrolling to the closest scrollable ancestor by calling
    // `scrollIntoView` on a sentinel placed after the last row — this
    // way the activity timeline doesn't need its own overflow container
    // (the panel body owns the scroll) and still pins the user to the
    // newest activity. The "near the bottom" guard avoids yanking the
    // user back if they've scrolled up to read history.
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (order !== 'chronological') return;
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        // Walk up to find the nearest scrollable ancestor.
        let scrollable: HTMLElement | null = sentinel.parentElement;
        while (scrollable) {
            const cs = window.getComputedStyle(scrollable);
            if (
                (cs.overflowY === 'auto' || cs.overflowY === 'scroll')
                && scrollable.scrollHeight > scrollable.clientHeight
            ) {
                break;
            }
            scrollable = scrollable.parentElement;
        }
        if (!scrollable) {
            sentinel.scrollIntoView({ block: 'end' });
            return;
        }
        const distanceFromBottom = scrollable.scrollHeight - scrollable.scrollTop - scrollable.clientHeight;
        if (distanceFromBottom < 120) {
            sentinel.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }
    }, [visibleParts.length, order]);

    // Pills stay hidden while a turn is in flight — the live activities
    // are the focus during execution, so navigation chrome would just
    // add noise. They reappear once the turn closes so the user can
    // browse history.
    const pillStrip = showTurnHistory && turns.length > 0 && !turnOpen
        ? (
            <TurnPillStrip
                turns={turns}
                selectedKey={selectedKey}
                onSelect={setSelectedKey}
            />
        )
        : null;

    if (visibleParts.length === 0) {
        return (
            <>
                <style>{`${spinKeyframes}${fadeKeyframes}${pulseKeyframes}`}</style>
                {pillStrip}
                {emptyState ?? null}
            </>
        );
    }

    // The "live" part — the one that's actively streaming — is the last
    // chronologically. With newest-first that becomes index 0.
    const liveIdx = order === 'newest-first' ? 0 : visibleParts.length - 1;

    return (
        <>
            <style>{`${spinKeyframes}${fadeKeyframes}${pulseKeyframes}`}</style>
            {pillStrip}
            <Box sx={{ py: 1, minHeight:'410px', maxHeight, overflow: maxHeight ? 'auto' : 'visible' }}>
                {visibleParts.map((part, i) => {
                    if (part.eventType === 'thought') {
                        if (hideReasoning) return null;
                        const isLast = i === liveIdx;
                        return (
                            <ReasoningRow
                                key={part.id}
                                part={part}
                                isStreaming={isLast && turnOpen && part.partial}
                            />
                        );
                    }
                    // Render every function-event variant — when an
                    // agent's tool response comes back, the runtime
                    // mutates the existing part's eventType from
                    // 'function_call' to 'function_response' (same
                    // object, response merged into functionDetails),
                    // so without including 'function_response' here we
                    // only ever see the currently in-flight tool call.
                    if (
                        part.eventType === 'function_call' ||
                        part.eventType === 'partial_function_call' ||
                        part.eventType === 'function_response'
                    ) {
                        return <ToolRow key={part.id} part={part} isLast={i === liveIdx} compact={compact} />;
                    }
                    return null;
                })}
                {/* Auto-scroll target — see the effect above. */}
                <Box ref={sentinelRef} aria-hidden sx={{ height: 0 }} />
            </Box>
        </>
    );
};

AgentActivityV2.displayName = 'AgentActivityV2';
